import { HttpErrorResponse } from '@angular/common/http';
import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  Injector,
  signal,
  viewChildren,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { catchError, finalize, map, Observable, of, startWith, switchMap, takeUntil } from 'rxjs';

import {
  PLAY_REQUEST_STATUSES,
  PLAYERS_CONTACT_PATH,
} from '../../../../../shared/players/players.constants';
import { PLAYERS_COPY } from '../../../../../shared/players/players.copy';
import type {
  PlayRequestAction,
  PlayRequestBox,
  PlayRequestItem,
  PlayRequestsResponse,
  PlayRequestStatus,
} from '../../../../../shared/players/players.model';
import { PlayersService } from '../../../../../shared/players/players.service';
import {
  aliasInitials,
  formatRequestDate,
  playerLocationLabel,
  playRequestContactView,
  serverMessage,
} from '../../../../../shared/players/players.utils';
import { playersSessionIdentity } from '../../../../../shared/players/players-session';
import { PaginationComponent } from '../../../../../shared/ui/pagination/pagination.component';

type ListState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'loaded'; response: PlayRequestsResponse }
  | { kind: 'error' };

const SKELETON_ROWS = [1, 2, 3] as const;

/**
 * Bandejas, historial y contador de solicitudes para jugar (B11 J3, D33-D34), en el hueco
 * reservado de la pestaña «Comunidad».
 *
 * - **Atadas a la cuenta resuelta** (como la pestaña, H1; pendiente mientras `auth.me()` está
 *   en vuelo, Astra J3 H4): la clave es cuenta + bandeja + estado +
 *   página + reintento, con `switchMap`; una respuesta tardía de otra bandeja o cuenta
 *   nunca pinta (MK6). Las acciones guardan además la cuenta con la que empezaron.
 * - **Acciones solo según `can`**; un elemento con una acción en vuelo no acepta otra.
 *   200 → el elemento se sustituye por `data` y se pinta el `message` del servidor.
 *   409/403/404 → texto del servidor y recarga de la bandeja.
 * - «Dejar de compartir contacto» pide confirmación en la página (foco a «Cancelar»).
 * - **Contacto**: enlace solo con un valor validado (`playRequestContactView`); mensaje y
 *   juego, texto plano.
 * - **Contador** (`getSummary`) al entrar y tras cada acción; «Recibidas (n)» si n > 0.
 */
@Component({
  selector: 'app-play-requests',
  imports: [PaginationComponent, RouterLink],
  templateUrl: './play-requests.component.html',
  styleUrl: './play-requests.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'block' },
})
export class PlayRequestsComponent {
  private readonly players = inject(PlayersService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);

  protected readonly copy = PLAYERS_COPY.requests;
  protected readonly reportCopy = PLAYERS_COPY.directory.report;
  protected readonly contactPath = PLAYERS_CONTACT_PATH;
  protected readonly statuses = PLAY_REQUEST_STATUSES;
  protected readonly skeletonRows = SKELETON_ROWS;

  /** «Recibidas» por defecto: los enlaces `received` del directorio llevan aquí (D34). */
  protected readonly box = signal<PlayRequestBox>('inbox');
  protected readonly status = signal<PlayRequestStatus | null>(null);
  protected readonly page = signal(1);
  private readonly retryTick = signal(0);
  /** La página ya se corrigió una vez desde el último cambio pedido (D2/D3: sin bucle). */
  private pageCorrected = false;
  private readonly summaryTick = signal(0);

  private readonly list = signal<ListState>({ kind: 'idle' });
  protected readonly pendingReceived = signal(0);
  protected readonly notice = signal<{ tone: 'info' | 'error'; text: string } | null>(null);
  /** Ids con una acción en vuelo (no aceptan otra). */
  protected readonly busy = signal<ReadonlySet<number>>(new Set());
  /** Id con la confirmación de «Dejar de compartir contacto» abierta. */
  protected readonly confirmingClose = signal<number | null>(null);
  private emptyMascotFailed = signal(false);
  protected readonly emptyMascotError = this.emptyMascotFailed.asReadonly();

  private readonly closeButtons = viewChildren<ElementRef<HTMLButtonElement>>('closeButton');
  private readonly closeCancelButtons = viewChildren<ElementRef<HTMLButtonElement>>('closeCancelButton');

  /** Identidad resuelta; `null` sin sesión o con `auth.me()` en vuelo (Astra J3 H4): esqueleto, sin datos ni acciones. */
  private readonly session = playersSessionIdentity();
  private readonly accountId = this.session.accountId;

  protected readonly loading = computed(() => {
    const kind = this.list().kind;
    return kind === 'loading' || kind === 'idle';
  });
  protected readonly failed = computed(() => this.list().kind === 'error');
  protected readonly items = computed(() => {
    const list = this.list();
    return list.kind === 'loaded' ? list.response.data : [];
  });
  protected readonly meta = computed(() => {
    const list = this.list();
    return list.kind === 'loaded' ? list.response.meta : null;
  });

  protected readonly inboxLabel = computed(() =>
    this.pendingReceived() > 0 ? this.copy.inboxWithCount(this.pendingReceived()) : this.copy.inbox,
  );

  protected readonly emptyText = computed(() => {
    if (this.status() !== null) return this.copy.emptyFiltered;
    return this.box() === 'inbox' ? this.copy.emptyInbox : this.copy.emptySent;
  });

  constructor() {
    const key = computed(() => ({
      account: this.accountId(),
      query: { box: this.box(), status: this.status(), page: this.page() },
      tick: this.retryTick(),
    }));
    toObservable(key)
      .pipe(
        switchMap(({ account, query }): Observable<ListState> => {
          if (account === null) return of({ kind: 'idle' });
          return this.players.getRequests(query).pipe(
            map((response): ListState => ({ kind: 'loaded', response })),
            catchError(() => of<ListState>({ kind: 'error' })),
            startWith<ListState>({ kind: 'loading' }),
          );
        }),
        takeUntilDestroyed(),
      )
      .subscribe((state) => {
        this.list.set(state);
        if (state.kind === 'loaded') this.reconcilePage(state.response.meta);
      });

    // Contador: cuelga de la cuenta (cambiarla lo vuelve a pedir y cancela el anterior) y, dentro
    // de la misma cuenta, de `summaryTick` (tras cada acción, sin vaciarse: no parpadea).
    const summaryTick$ = toObservable(this.summaryTick);
    toObservable(this.accountId)
      .pipe(
        switchMap((account) =>
          account === null
            ? of(0)
            : summaryTick$.pipe(
                switchMap(() => this.players.getSummary().pipe(catchError(() => of(0)))),
              ),
        ),
        takeUntilDestroyed(),
      )
      .subscribe((count) => this.pendingReceived.set(count));

    // Cambio de identidad (también a pendiente y a null): nada de lo anterior sigue en pantalla.
    this.session.changes$
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        // El contador de la identidad anterior se va en el acto, antes de pedir el nuevo (Astra J3 H1).
        this.pendingReceived.set(0);
        this.pageCorrected = false;
        this.notice.set(null);
        this.confirmingClose.set(null);
        this.busy.set(new Set());
        this.box.set('inbox');
        this.status.set(null);
        this.page.set(1);
      });
  }

  // ── Filtros ───────────────────────────────────────────────────────────

  /**
   * La página local se reconcilia con la respuesta (Astra J3 H2, D2/D3): una recarga puede
   * dejarla fuera de rango (la última fila salió del filtro, un 409/403/404, o no queda
   * ninguna) y la API puede recortar la página pedida. Válida = `min(local, max(1, total_pages))`
   * o la `meta.page` de la API si es válida y menor. **Una sola corrección** por origen: se
   * rearma con cada cambio pedido por la persona (bandeja, estado, página, reintento) o de
   * identidad; así una API incoherente provoca como mucho una petición extra.
   */
  private reconcilePage(meta: PlayRequestsResponse['meta']): void {
    if (this.pageCorrected) return;
    const local = this.page();
    const max = Math.max(1, meta.total_pages);
    let target = Math.min(local, max);
    if (Number.isInteger(meta.page) && meta.page >= 1 && meta.page <= max && meta.page < target) target = meta.page;
    if (target === local) return;
    this.pageCorrected = true;
    this.page.set(target);
  }

  protected selectBox(box: PlayRequestBox): void {
    this.pageCorrected = false;
    if (box === this.box()) return;
    this.notice.set(null);
    this.confirmingClose.set(null);
    this.box.set(box);
    this.page.set(1);
  }

  protected onStatusChange(event: Event): void {
    this.pageCorrected = false;
    const value = (event.target as HTMLSelectElement).value as PlayRequestStatus | '';
    this.notice.set(null);
    this.confirmingClose.set(null);
    this.status.set(value === '' ? null : value);
    this.page.set(1);
  }

  protected goToPage(page: number): void {
    this.pageCorrected = false;
    this.notice.set(null);
    this.confirmingClose.set(null);
    this.page.set(page);
  }

  protected retry(): void {
    this.pageCorrected = false;
    this.retryTick.update((tick) => tick + 1);
  }

  // ── Vista de cada elemento ───────────────────────────────────────────

  protected statusLabel(item: PlayRequestItem): string {
    return this.copy.statusLabels[item.status];
  }

  protected closedByText(item: PlayRequestItem): string {
    if (item.closed_by === null) return '';
    const byStatus = this.copy.closedBy[item.status as keyof typeof this.copy.closedBy];
    return byStatus ? byStatus[item.closed_by] : '';
  }

  protected date(iso: string | null): string {
    return formatRequestDate(iso);
  }

  protected initials(alias: string): string {
    return aliasInitials(alias);
  }

  protected location(item: PlayRequestItem): string {
    const counterpart = item.counterpart;
    return counterpart.available ? playerLocationLabel(counterpart) : '';
  }

  protected contactView(item: PlayRequestItem) {
    return item.contact ? playRequestContactView(item.contact) : null;
  }

  protected isBusy(id: number): boolean {
    return this.busy().has(id);
  }

  // ── Acciones ─────────────────────────────────────────────────────────

  protected askClose(item: PlayRequestItem): void {
    if (!item.can.close || this.isBusy(item.id)) return;
    this.notice.set(null);
    this.confirmingClose.set(item.id);
    afterNextRender(
      () => this.closeCancelButtons().find((b) => b.nativeElement.dataset['id'] === String(item.id))?.nativeElement.focus(),
      { injector: this.injector },
    );
  }

  protected cancelClose(item: PlayRequestItem): void {
    this.confirmingClose.set(null);
    afterNextRender(
      () => this.closeButtons().find((b) => b.nativeElement.dataset['id'] === String(item.id))?.nativeElement.focus(),
      { injector: this.injector },
    );
  }

  protected run(item: PlayRequestItem, action: PlayRequestAction): void {
    if (!item.can[action] || this.isBusy(item.id)) return;
    // Época de sesión (Astra J3 H3): el `id` solo no basta, porque A → B → A vuelve a coincidir.
    const account = this.accountId();
    const epoch = this.session.epoch();
    const sameAccount = () => this.session.epoch() === epoch && this.accountId() === account;
    this.notice.set(null);
    this.setBusy(item.id, true);
    this.players[action](item.id)
      .pipe(
        // Y se cancela al cambiar la identidad (defensa en profundidad con la época).
        takeUntil(this.session.changes$),
        finalize(() => {
          if (sameAccount()) this.setBusy(item.id, false);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (result) => {
          if (!sameAccount()) return;
          this.confirmingClose.set(null);
          // Con filtro de estado, una fila que ya no casa con él sale: se recarga la bandeja (total
          // y página incluidos, Astra J3 H2). Sin filtro se sustituye en el sitio, que así se ve el
          // contacto recién compartido.
          const filter = this.status();
          if (filter !== null && result.data.status !== filter) this.retry();
          else this.replace(result.data);
          this.notice.set({ tone: 'info', text: result.message ?? this.copy.actionFallback[action] });
          this.summaryTick.update((tick) => tick + 1);
        },
        error: (err: unknown) => {
          if (!sameAccount()) return;
          this.confirmingClose.set(null);
          this.onActionError(err);
        },
      });
  }

  private onActionError(err: unknown): void {
    const status = err instanceof HttpErrorResponse ? err.status : 0;
    if (status === 409 || status === 403 || status === 404) {
      const fallback = status === 404 ? this.copy.notFoundRequestFallback : this.copy.alreadyResolvedFallback;
      this.notice.set({ tone: 'error', text: serverMessage(err) ?? fallback });
      this.retry();
      this.summaryTick.update((tick) => tick + 1);
      return;
    }
    this.notice.set({ tone: 'error', text: this.copy.actionError });
  }

  private replace(updated: PlayRequestItem): void {
    this.list.update((state) =>
      state.kind === 'loaded'
        ? {
            ...state,
            response: {
              ...state.response,
              data: state.response.data.map((item) => (item.id === updated.id ? updated : item)),
            },
          }
        : state,
    );
  }

  private setBusy(id: number, busy: boolean): void {
    this.busy.update((set) => {
      const next = new Set(set);
      if (busy) next.add(id);
      else next.delete(id);
      return next;
    });
  }

  protected onEmptyMascotError(): void {
    this.emptyMascotFailed.set(true);
  }
}
